import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { updateUserToCoach } from "../../api/userApi";

const ConvertToCoachModal = ({ openCoachModal, setOpenCoachModal }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    outline: "none"
  };

  const handleClose = () => {
    if (!loading) {
      setOpenCoachModal(false);
      setEmail("");
      setError("");
      setSuccess("");
    }
  };

  const handleConfirm = async () => {
    if (!email) {
      setError("Please enter an email address");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await updateUserToCoach(email?.trim());

      setSuccess("User successfully converted to coach!");

      // Close modal after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      const errorMessage =
        err?.error ||
        err.response?.data?.error ||
        err.response?.data?.message ||
        "An error occurred while converting user to coach";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError(""); // Clear error when user starts typing
  };

  return (
    <Modal
      open={openCoachModal}
      onClose={handleClose}
      aria-labelledby="convert-coach-modal-title"
      aria-describedby="convert-coach-modal-description"
    >
      <Box sx={modalStyle}>
        {/* Header with close button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3
          }}
        >
          <Typography
            id="convert-coach-modal-title"
            variant="h5"
            component="h2"
            fontWeight="bold"
          >
            Convert User to Coach
          </Typography>
          <IconButton onClick={handleClose} disabled={loading} sx={{ p: 0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Instructions */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            This action will:
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            component="ul"
            sx={{ pl: 2, mb: 0 }}
          >
            <li>
              Create a new coach profile with default settings (25 max seats)
            </li>
            <li>
              Update the user's type to "Coach" and grant admin privileges
            </li>
            <li>Generate a unique coach code for identification</li>
            <li>Cancel any active Stripe subscriptions</li>
            <li>
              Temporarily deactivate the user account during conversion so users
              can log in on admin portal and re-activate as a coach
            </li>
          </Typography>
        </Box>

        {/* Email Input */}
        <TextField
          fullWidth
          label="User Email"
          type="email"
          value={email}
          onChange={handleEmailChange}
          disabled={loading}
          placeholder="Enter the user's email address"
          sx={{ mb: 3 }}
          autoFocus
        />

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={loading || !email.trim()}
            sx={{ minWidth: 100 }}
            startIcon={
              loading && <CircularProgress size={16} color="inherit" />
            }
          >
            {loading ? "Converting..." : "Confirm"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConvertToCoachModal;
